/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseNode): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const inputs = feature.getInputs();
    const ID = feature.getID();
    const METHOD = feature.getSettingValue('METHOD');
    const INTERFACE = 'current_interface';
    const URL = `${feature.getSettingValue('URL')}`;
    const HEADERS = feature.getSettingValue('HEADERS');
    const CERTIFICATEFILE = feature.getSettingValue('CERTIFICATEFILE');
    let links = project.getNodeLinks(feature, 'http_request_start');
    const urlVariableName = `http_request_url_${feature.getID()}`;
    let dataLines = [];
    if (links.length > 0) {
        const DATA = project.getNodeParameterValue(feature, 'data');
        // determine how many newlines are in the DATA
        dataLines = DATA.split('\n');
        if (dataLines.length > 1) {
            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
declare http_request_${ID}(${dataLines.length}) as string
declare http_request_${ID}_data_index as byte`,
            });
            blocks.push({
                location: 'GLOBAL_DIMS',
                blockType: BlockType.CODE,
                template: `
dim http_request_${ID}(${dataLines.length}) as string
dim http_request_${ID}_data_index as byte=0`,
            });
            blocks.push({
                location: 'callback_http_send_post_data',
                blockType: BlockType.CODE,
                template: `
if http_request_item.url = ${urlVariableName} then
    http_send_post_data(http_request_${ID}(http_request_${ID}_data_index))
    http_request_${ID}_data_index = http_request_${ID}_data_index + 1
end if`,
            });
        }
    }

    for (let i = 0; i < links.length; i += 1) {
        const link = links[i];
        const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
        const sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
        const data = project.getNodeParameterValue(feature, 'data');
        if (dataLines.length > 1) {
            let dataCode = '';
            for (let j = 0; j < dataLines.length; j += 1) {
                const dataLine = project.parseCodeExpression(dataLines[j], sourceParams, TibboDataType.STRING);
                dataCode += `http_request_${ID}(${j}) = ${dataLine}\n`;
            }
            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `
${dataCode}
dim content_length_${ID} as dword=0
dim i_${ID} as byte=0
for i_${ID}=0 to ${dataLines.length}-1
    content_length_${ID} = content_length_${ID} + len(http_request_${ID}(i_${ID}))
next i_${ID}
http_request_${ID}_data_index = 0
${urlVariableName}=${project.parseCodeExpression(URL, sourceParams, TibboDataType.STRING)}
http_request_long(${METHOD}, ${urlVariableName}, ${INTERFACE}, content_length_${ID})`,
            });
        } else {
            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `
${urlVariableName}=${project.parseCodeExpression(URL, sourceParams, TibboDataType.STRING)}
http_request(${METHOD}, ${urlVariableName}, ${INTERFACE},
${project.parseCodeExpression(data, sourceParams, TibboDataType.STRING)})`,
            });
        }
    }

    if (CERTIFICATEFILE !== '') {
        blocks.push({
            location: 'callback_http_open_romfile',
            blockType: BlockType.CODE,
            template: `
    if http_request_item.url = ${urlVariableName} then
        callback_http_open_romfile=0
        romfile.open("${CERTIFICATEFILE.split('\n')[0]}")
        callback_http_open_romfile=romfile.offset    
    end if
        `,
        });
    }

    let headerCode = '';
    for (let j = 0; j < HEADERS.length; j += 1) {
        const header = HEADERS[j];
        headerCode += `http_send_headers("${header.getSettingValue('NAME')}","${header.getSettingValue('VALUE')}")\r\n`;
    }
    // callback_http_send_headers
    blocks.push({
        location: 'callback_http_send_headers',
        blockType: BlockType.CODE,
        template: `
if http_request_item.url = ${urlVariableName} then
    ${headerCode}
end if`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        id: `declare_${urlVariableName}`,
        template: `
declare ${urlVariableName} as string`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        id: `dim_${urlVariableName}`,
        template: `
dim ${urlVariableName} as string = ""`,
    });


    links = project.getNodeLinks(feature, 'http_data_arrival');
    if (links.length > 0) {
        const link = links[0];
        blocks.push({
            location: 'callback_http_content_arrival',
            blockType: BlockType.CODE,
            id: `callback_http_content_arrival_${feature.getID()}`,
            template: `
if http_request_item.url = ${urlVariableName} then
    '****${link.getSourceId()}****'
end if`,
        });
    }

    links = project.getNodeLinks(feature, 'http_complete');
    if (links.length > 0) {
        const link = links[0];
        blocks.push({
            location: 'callback_http_request_complete',
            blockType: BlockType.CODE,
            id: `callback_http_request_complete_${feature.getID()}`,
            template: `
if http_request_item.url = ${urlVariableName} then
    '****${link.getSourceId()}****'
end if`,
        });
    }

    links = project.getNodeLinks(feature, 'http_error');
    if (links.length > 0) {
        const link = links[0];
        blocks.push({
            location: 'callback_http_request_failed',
            blockType: BlockType.CODE,
            id: `callback_http_request_failed_${feature.getID()}`,
            template: `
if http_request_item.url = ${urlVariableName} then
    '****${link.getSourceId()}****'
end if`,
        });
    }

    return blocks;
};
